
import { useState } from 'react';
import { Link } from 'react-router-dom';

interface CalculatorResult {
  eligibilityRate: { min: number; max: number };
  processingTime: string;
  difficulty: 'Düşük' | 'Orta' | 'Yüksek';
  consultingLevel: 'Standart' | 'Premium';
}

export default function VisaCalculator() {
  const [selectedVisaType, setSelectedVisaType] = useState('schengen-tourist');
  const [travelDuration, setTravelDuration] = useState(15);
  const [previousVisa, setPreviousVisa] = useState<'yes' | 'no'>('no');
  const [employmentStatus, setEmploymentStatus] = useState('employee');

  const visaTypes = [
    { id: 'schengen-tourist', label: 'Schengen Turistik', icon: 'ri-plane-line' },
    { id: 'schengen-business', label: 'Schengen Ticari', icon: 'ri-briefcase-line' },
    { id: 'uk', label: 'İngiltere Vizesi', icon: 'ri-building-line' },
    { id: 'usa', label: 'Amerika Vizesi', icon: 'ri-flag-line' },
    { id: 'canada', label: 'Kanada Vizesi', icon: 'ri-map-pin-line' },
    { id: 'family-student', label: 'Aile Birleşimi / Öğrenci', icon: 'ri-group-line' },
  ];

  const employmentOptions = [
    { id: 'employee', label: 'Çalışan', icon: 'ri-user-line' },
    { id: 'employer', label: 'İşveren', icon: 'ri-briefcase-4-line' },
    { id: 'student', label: 'Öğrenci', icon: 'ri-book-open-line' },
    { id: 'retired', label: 'Emekli', icon: 'ri-user-heart-line' },
    { id: 'freelance', label: 'Serbest Meslek', icon: 'ri-pencil-ruler-2-line' },
  ];

  const calculateResult = (): CalculatorResult => {
    let baseRate = 50;
    let difficulty: 'Düşük' | 'Orta' | 'Yüksek' = 'Orta';
    let consultingLevel: 'Standart' | 'Premium' = 'Standart';
    let processingTime = '15 – 30 Gün';

    // Vize türüne göre
    if (selectedVisaType === 'schengen-tourist') {
      baseRate += 15;
      difficulty = 'Düşük';
    } else if (selectedVisaType === 'usa' || selectedVisaType === 'uk') {
      baseRate += 5;
      difficulty = 'Yüksek';
      consultingLevel = 'Premium';
      processingTime = '30 – 60 Gün';
    } else if (selectedVisaType === 'family-student') {
      baseRate += 10;
      difficulty = 'Orta';
      processingTime = '45 – 90 Gün';
    }

    // Önceki vize deneyimi
    if (previousVisa === 'yes') {
      baseRate += 20;
    }

    // Çalışma durumu
    if (employmentStatus === 'employee' || employmentStatus === 'employer') {
      baseRate += 15;
    } else if (employmentStatus === 'retired') {
      baseRate += 10;
    } else if (employmentStatus === 'student') {
      baseRate += 5;
    }

    // Seyahat süresi
    if (travelDuration <= 14) {
      baseRate += 10;
    } else if (travelDuration >= 60) {
      baseRate -= 5;
    }

    const min = Math.min(Math.max(baseRate - 10, 40), 85);
    const max = Math.min(Math.max(baseRate + 10, 50), 95);

    return {
      eligibilityRate: { min, max },
      processingTime,
      difficulty,
      consultingLevel,
    };
  };

  const result = calculateResult();

  const whatsappMessage = encodeURIComponent(
    'Merhaba, web sitenizdeki vize değerlendirme aracını doldurdum. Dosyam hakkında bilgi almak istiyorum.'
  );
  const whatsappUrl = `https://wa.me/905422671171?text=${whatsappMessage}`;

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-b from-white via-lightBlue/30 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="font-heading font-bold text-3xl lg:text-4xl text-darkBlue mb-4">
            Vize Uygunluk ve Ön Değerlendirme Hesaplayıcı
          </h2>
          <p className="font-body text-lg text-gray-600 max-w-2xl mx-auto">
            Bilgilerinize göre vize uygunluğunuzu ve süreci saniyeler içinde öğrenin.
          </p>
        </div>

        {/* Calculator Grid */}
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Left Side - User Inputs */}
          <div className="lg:col-span-3 space-y-8">
            {/* 1. Visa Type Selection */}
            <div>
              <h3 className="font-heading font-bold text-xl text-darkBlue mb-4">
                Vize Türü Seçin
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {visaTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setSelectedVisaType(type.id)}
                    className={`p-4 rounded-xl border-2 transition-all duration-300 text-center hover:scale-105 ${
                      selectedVisaType === type.id
                        ? 'border-secondary bg-secondary/10 shadow-lg'
                        : 'border-gray-200 bg-white hover:border-secondary/50'
                    }`}
                  >
                    <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                      <i
                        className={`${type.icon} text-2xl ${
                          selectedVisaType === type.id ? 'text-secondary' : 'text-gray-400'
                        }`}
                      ></i>
                    </div>
                    <span
                      className={`font-body text-sm font-semibold ${
                        selectedVisaType === type.id ? 'text-secondary' : 'text-gray-700'
                      }`}
                    >
                      {type.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* 2. Travel Duration Slider */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-bold text-xl text-darkBlue">
                  Planlanan Seyahat Süresi
                </h3>
                <span className="font-heading font-bold text-2xl text-secondary">
                  {travelDuration} Gün
                </span>
              </div>
              <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
                <input
                  type="range"
                  min="7"
                  max="90"
                  value={travelDuration}
                  onChange={(e) => setTravelDuration(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-secondary"
                  style={{
                    background: `linear-gradient(to right, #FF6B35 0%, #FF6B35 ${
                      ((travelDuration - 7) / (90 - 7)) * 100
                    }%, #e5e7eb ${((travelDuration - 7) / (90 - 7)) * 100}%, #e5e7eb 100%)`,
                  }}
                />
                <div className="flex justify-between mt-2">
                  <span className="font-body text-xs text-gray-500">7 Gün</span>
                  <span className="font-body text-xs text-gray-500">90 Gün</span>
                </div>
              </div>
            </div>

            {/* 3. Previous Visa */}
            <div>
              <h3 className="font-heading font-bold text-xl text-darkBlue mb-4">
                Daha Önce Vize Aldınız mı?
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setPreviousVisa('yes')}
                  className={`p-4 rounded-xl border-2 transition-all duration-300 font-body font-semibold ${
                    previousVisa === 'yes'
                      ? 'border-secondary bg-secondary/10 text-secondary shadow-lg'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-secondary/50'
                  }`}
                >
                  <i className="ri-checkbox-circle-line text-2xl mb-2"></i>
                  <div>Evet</div>
                </button>
                <button
                  onClick={() => setPreviousVisa('no')}
                  className={`p-4 rounded-xl border-2 transition-all duration-300 font-body font-semibold ${
                    previousVisa === 'no'
                      ? 'border-secondary bg-secondary/10 text-secondary shadow-lg'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-secondary/50'
                  }`}
                >
                  <i className="ri-close-circle-line text-2xl mb-2"></i>
                  <div>Hayır</div>
                </button>
              </div>
            </div>

            {/* 4. Employment Status */}
            <div>
              <h3 className="font-heading font-bold text-xl text-darkBlue mb-4">
                Çalışma Durumu
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {employmentOptions.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setEmploymentStatus(option.id)}
                    className={`p-4 rounded-xl border-2 transition-all duration-300 text-center ${
                      employmentStatus === option.id
                        ? 'border-secondary bg-secondary/10 shadow-lg'
                        : 'border-gray-200 bg-white hover:border-secondary/50'
                    }`}
                  >
                    <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                      <i
                        className={`${option.icon} text-xl ${
                          employmentStatus === option.id ? 'text-secondary' : 'text-gray-400'
                        }`}
                      ></i>
                    </div>
                    <span
                      className={`font-body text-sm font-semibold ${
                        employmentStatus === option.id ? 'text-secondary' : 'text-gray-700'
                      }`}
                    >
                      {option.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right Side - Result Panel */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border-2 border-secondary/20 shadow-xl p-6 lg:p-8 lg:sticky lg:top-24">
              <h3 className="font-heading font-bold text-2xl text-darkBlue mb-6 text-center">
                Tahmini Vize Değerlendirmesi
              </h3>

              {/* Eligibility Rate */}
              <div className="bg-gradient-to-br from-secondary/10 to-primary/10 rounded-xl p-6 mb-6 text-center">
                <div className="font-body text-sm text-gray-600 mb-2">Uygunluk Oranı</div>
                <div className="font-heading font-bold text-4xl lg:text-5xl text-secondary mb-2">
                  %{result.eligibilityRate.min} – %{result.eligibilityRate.max}
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 mt-4">
                  <div
                    className="bg-gradient-to-r from-secondary to-primary h-3 rounded-full transition-all duration-500"
                    style={{ width: `${result.eligibilityRate.max}%` }}
                  ></div>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-primary/10 rounded-full">
                      <i className="ri-time-line text-primary"></i>
                    </div>
                    <span className="font-body text-sm text-gray-600">Tahmini Sonuç Süresi</span>
                  </div>
                  <span className="font-body font-bold text-darkBlue text-sm">
                    {result.processingTime}
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-secondary/10 rounded-full">
                      <i className="ri-file-list-3-line text-secondary"></i>
                    </div>
                    <span className="font-body text-sm text-gray-600">Dosya Zorluk Seviyesi</span>
                  </div>
                  <span
                    className={`font-body font-bold text-sm ${
                      result.difficulty === 'Düşük'
                        ? 'text-green-600'
                        : result.difficulty === 'Orta'
                        ? 'text-orange-500'
                        : 'text-red-600'
                    }`}
                  >
                    {result.difficulty}
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-primary/10 rounded-full">
                      <i className="ri-star-line text-primary"></i>
                    </div>
                    <span className="font-body text-sm text-gray-600">Danışmanlık Seviyesi</span>
                  </div>
                  <span className="font-body font-bold text-darkBlue text-sm">
                    {result.consultingLevel}
                  </span>
                </div>
              </div>

              {/* Trust Points */}
              <div className="bg-lightBlue/30 rounded-xl p-4 mb-6">
                <div className="space-y-2">
                  <div className="flex items-start space-x-2">
                    <i className="ri-checkbox-circle-fill text-secondary text-lg mt-0.5"></i>
                    <span className="font-body text-sm text-gray-700">
                      Ücretsiz Ön Değerlendirme
                    </span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <i className="ri-checkbox-circle-fill text-secondary text-lg mt-0.5"></i>
                    <span className="font-body text-sm text-gray-700">
                      Evrak Kontrol ve Dosya Hazırlığı
                    </span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <i className="ri-checkbox-circle-fill text-secondary text-lg mt-0.5"></i>
                    <span className="font-body text-sm text-gray-700">
                      Randevu ve Başvuru Takibi
                    </span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <i className="ri-checkbox-circle-fill text-secondary text-lg mt-0.5"></i>
                    <span className="font-body text-sm text-gray-700">
                      Red Riskini Azaltmaya Yönelik Danışmanlık
                    </span>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="space-y-3">
                <Link
                  to="/iletisim"
                  className="w-full bg-secondary hover:bg-secondary/90 text-white px-6 py-4 rounded-full font-body font-bold transition-all shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 whitespace-nowrap"
                >
                  <span>Ücretsiz Ön Değerlendirme Al</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>

                <div className="grid grid-cols-2 gap-3">
                  <a
                    href="tel:+905422671171"
                    className="bg-white hover:bg-gray-50 text-secondary border-2 border-secondary px-4 py-3 rounded-full font-body font-semibold text-sm transition-all flex items-center justify-center space-x-2 whitespace-nowrap"
                  >
                    <i className="ri-phone-line"></i>
                    <span>Hemen Ara</span>
                  </a>

                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#25D366] hover:bg-[#20BA5A] text-white px-4 py-3 rounded-full font-body font-semibold text-sm transition-all flex items-center justify-center space-x-2 whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line"></i>
                    <span>WhatsApp</span>
                  </a>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <p className="font-body text-xs text-gray-500 text-center leading-relaxed">
                  * Gösterilen sonuçlar ön değerlendirmedir. Kesin karar ilgili konsolosluğa
                  aittir. Bilgileriniz KVKK kapsamında korunmaktadır.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
